import React, { useContext, useRef, useState } from "react";
import styled from "styled-components";
import AniLink from "gatsby-plugin-transition-link/AniLink";

import context from "../context";
import RateBar from "./ratebar";
import Logo from "./logo";
import { NavLink } from "../styled-components";

/* Header */
const Header = styled.header`
  background-color: ${(props) => props.theme.primaryColor};
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1000;
  overflow: visible;
`;

const HeaderContent = styled.div`
  max-width: 1440px;
  width: 100%;
  margin: 0 auto;
  padding: 0 clamp(12px, 2vw, 28px);
  overflow: visible;
`;

const Navigation = styled.nav`
  padding: clamp(0.75rem, 2vw, 1rem) 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: nowrap;
  gap: clamp(0.5rem, 1vw, 1rem);
  overflow: visible;
`;

const Brand = styled.div`
  flex: 0 0 auto;
  min-width: 0;
`;

const NavList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;

  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  white-space: nowrap;
  gap: clamp(0.6rem, 1.2vw, 1.6rem);

  overflow: visible;
`;

const NavItem = styled.li`
  position: relative;
  flex: 0 0 auto;
  overflow: visible;
`;

const MenuLink = styled(NavLink)`
  display: inline-flex;
  align-items: center;
  font-size: clamp(0.85rem, 1vw, 1rem);
  white-space: nowrap;
`;

const AccentButton = styled(NavLink)`
  display: inline-flex;
  align-items: center;
  justify-content: center;

  background-color: #f9a200;
  color: #fff !important;
  border-radius: 999px;

  /* ✅ fuente distinta SOLO para este botón */
  font-family: "Montserrat", "Poppins", "Helvetica Neue", Arial, sans-serif !important;
  font-weight: 800;

  transition: background-color 0.3s ease;
  font-size: clamp(0.85rem, 1.1vw, 1rem);
  padding: clamp(0.45rem, 1.1vw, 0.6rem) clamp(0.85rem, 2.4vw, 1.3rem);

  white-space: nowrap;

  &:hover {
    background-color: #e08f00;
  }
`; 

/* Submenú de Propiedades */
const Submenu = styled.ul`
  list-style: none;
  padding: 8px 0;
  margin: 0;

  position: absolute;
  left: 0;
  top: calc(100% + 10px);

  min-width: 220px;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 14px 28px rgba(0, 0, 0, 0.18);

  opacity: ${(p) => (p.open ? 1 : 0)};
  transform: translateY(${(p) => (p.open ? "0" : "6px")});
  transition: opacity 0.15s ease, transform 0.15s ease;
  pointer-events: ${(p) => (p.open ? "auto" : "none")};

  z-index: 99999;

  &::before {
    content: "";
    position: absolute;
    top: -6px;
    left: 16px;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-bottom: 6px solid #fff;
  }
`;

const SubmenuItem = styled.li``;

const SubmenuLink = styled(AniLink)`
  display: block;
  padding: 10px 14px;
  color: #111 !important;
  font-weight: 600;
  text-decoration: none;
  transition: background 0.15s ease, color 0.15s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.06);
    color: ${(p) => p.theme.primaryColor} !important;
  }
`;

export default function DesktopHeader({ dark }) {
  const state = useContext(context);
  const [submenuOpen, setSubmenuOpen] = useState(false);

  // ✅ control de timer (no depende del state, no re-renderiza)
  const closeTimerRef = useRef(null);
  const CLOSE_DELAY_MS = 1000;

  const baseQuery =
    "?email=propiedadeslasflores%40gmail.com" +
    "&status=PUBLICADA%2CARRENDADA%2CVENDIDA" +
    "&limit=12" +
    "&typeId=office" +
    "&id=5e8e36b31c9d440000d35090" +
    "&propertyType=" +
    "&commune=" +
    "&stringSearch=" +
    "&priceMin=" +
    "&priceMax=" +
    "&totalAreaFrom=" +
    "&totalAreaTo=" +
    "&bedrooms=" +
    "&bathrooms=" +
    "&currency=CLP";

  const ventaHref = `/properties/${baseQuery}&operation=VENTA`;
  const arriendoHref = `/properties/${baseQuery}&operation=ARRIENDO`;
  const proyectosHref = "/proyectos";

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const openMenu = () => {
    clearCloseTimer();
    setSubmenuOpen(true);
  };

  const scheduleClose = () => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => {
      setSubmenuOpen(false);
      closeTimerRef.current = null;
    }, CLOSE_DELAY_MS);
  };

  const closeNow = () => {
    clearCloseTimer();
    setSubmenuOpen(false);
  };

  return (
    <Header className="d-none d-lg-block">
      <RateBar />
      <HeaderContent>
        <Navigation>
          <Brand>
            <AniLink paintDrip hex={state.primaryColor} to="/" duration={0.5}>
              <Logo dark={dark} light={!dark} />
            </AniLink>
          </Brand>

          <NavList>
            <NavItem>
              <AniLink paintDrip hex={state.primaryColor} to="/publica" duration={0.5}>
                <AccentButton dark={dark} light={!dark}>
                  Publica tu propiedad
                </AccentButton>
              </AniLink>
            </NavItem>

            <NavItem>
              <AniLink paintDrip hex={state.primaryColor} to="/in-construction" duration={0.5}>
                <MenuLink dark={dark} light={!dark}>
                  Las Flores Inmobiliarias
                </MenuLink>
              </AniLink>
            </NavItem>

            {/* ✅ submenu con delay de cierre */}
            <NavItem onMouseEnter={openMenu} onMouseLeave={scheduleClose}>
              <AniLink paintDrip hex={state.primaryColor} to="/properties" duration={0.5}>
                <MenuLink dark={dark} light={!dark}>
                  Propiedades
                </MenuLink>
              </AniLink>

              <Submenu
                open={submenuOpen}
                onMouseEnter={openMenu}   // ✅ si entras al submenu, NO se cierra
                onMouseLeave={scheduleClose} // ✅ al salir, espera 1s
              >
                <SubmenuItem>
                  <SubmenuLink paintDrip hex={state.primaryColor} to={ventaHref} duration={0.4} onClick={closeNow}>
                    Venta
                  </SubmenuLink>
                </SubmenuItem>

                <SubmenuItem>
                  <SubmenuLink paintDrip hex={state.primaryColor} to={arriendoHref} duration={0.4} onClick={closeNow}>
                    Arriendo
                  </SubmenuLink>
                </SubmenuItem>

                <SubmenuItem>
                  <SubmenuLink paintDrip hex={state.primaryColor} to={ventaHref} duration={0.4} onClick={closeNow}>
                    Proyectos
                  </SubmenuLink>
                </SubmenuItem>
              </Submenu>
            </NavItem>

            <NavItem>
              <AniLink paintDrip hex={state.primaryColor} to="/contact" duration={0.5}>
                <MenuLink dark={dark} light={!dark}>
                  Contáctanos
                </MenuLink>
              </AniLink>
            </NavItem>

            <NavItem>
              <AniLink paintDrip hex={state.primaryColor} to="/about" duration={0.5}>
                <MenuLink dark={dark} light={!dark}>
                  Nosotros
                </MenuLink>
              </AniLink>
            </NavItem>
          </NavList>
        </Navigation>
      </HeaderContent>
    </Header>
  );
}